import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  Input, Label,
} from '@erp/ui';
import { useAuthService } from '@/lib/service-context';

export interface AutoConfigureIntigoDialogProps {
  open: boolean;
  onClose: () => void;
  webhookUrl: string;
  webhookSecret: string;
  /** Called after a successful save so the parent can show a confirmation. */
  onSuccess?: () => void;
}

/** Asks the user for their Intigo dashboard credentials and posts them to
 *  /api/delivery/intigo/auto-configure-webhook so the server can replay
 *  the dashboard form. The credentials never touch our DB. */
export function AutoConfigureIntigoDialog(props: AutoConfigureIntigoDialogProps) {
  const { t } = useTranslation();
  const { open, onClose, webhookUrl, webhookSecret, onSuccess } = props;
  const authService = useAuthService();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const canSubmit = !!email.trim() && !!password && !!webhookUrl && !!webhookSecret && !submitting;

  function reset() {
    setEmail('');
    setPassword('');
    setError(null);
    setDone(false);
    setSubmitting(false);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const token = await authService.getAccessToken();
      if (!token) throw new Error('not_authenticated');

      const res = await fetch('/api/delivery/intigo/auto-configure-webhook', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
          webhookUrl,
          webhookToken: webhookSecret,
        }),
      });

      const payload = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !payload.ok) {
        const code = payload.error ?? `http_${res.status}`;
        throw new Error(translateError(code, (k, fb) => t(k, fb ?? '') as string));
      }
      // Wipe credentials from React state immediately after the call.
      setPassword('');
      setEmail('');
      setDone(true);
      onSuccess?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  function handleOpenChange(o: boolean) {
    if (!o) {
      reset();
      onClose();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('settings.deliveryProviders.autoConfig.title', 'Configurer le webhook Intigo automatiquement')}
          </DialogTitle>
        </DialogHeader>

        {!webhookSecret && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
            {t(
              'settings.deliveryProviders.autoConfig.needsSecret',
              "Cliquez d'abord « Générer » sur le secret webhook avant de lancer l'auto-config.",
            )}
          </div>
        )}

        {done ? (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm text-emerald-700 dark:text-emerald-400">
              {t(
                'settings.deliveryProviders.autoConfig.success',
                "Webhook configuré chez Intigo. Vos identifiants ont été utilisés une seule fois et ne sont pas stockés.",
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-300 space-y-1">
              <p className="font-medium">
                {t('settings.deliveryProviders.autoConfig.warningTitle', 'Pourquoi on demande votre mot de passe Intigo')}
              </p>
              <p>
                {t(
                  'settings.deliveryProviders.autoConfig.warningBody',
                  "Intigo n'expose pas d'API officielle pour configurer le webhook. Notre serveur va se connecter à votre dashboard avec vos identifiants, sauvegarder l'URL + secret, puis se déconnecter immédiatement. Les identifiants ne sont JAMAIS stockés et ne transitent que dans la requête actuelle.",
                )}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ac-email">
                {t('settings.deliveryProviders.autoConfig.email', 'Email Intigo (votre login dashboard)')}
              </Label>
              <Input
                id="ac-email"
                type="email"
                autoComplete="off"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="vous@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ac-password">
                {t('settings.deliveryProviders.autoConfig.password', 'Mot de passe Intigo')}
              </Label>
              <Input
                id="ac-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>

            <div className="rounded-md border bg-muted/30 p-2 text-[11px] space-y-1">
              <p className="text-muted-foreground">{t('settings.deliveryProviders.autoConfig.willSave', 'À enregistrer chez Intigo :')}</p>
              <p className="font-mono break-all">URL : {webhookUrl}</p>
              <p className="font-mono break-all">
                Secret : {webhookSecret ? '•••••••• (masqué)' : '— manquant —'}
              </p>
            </div>

            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                {error}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {done ? (
            <Button onClick={() => handleOpenChange(false)}>
              {t('common.close', 'Fermer')}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => handleOpenChange(false)}>
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                {submitting
                  ? t('settings.deliveryProviders.autoConfig.submitting', 'Configuration en cours…')
                  : t('settings.deliveryProviders.autoConfig.submit', 'Configurer chez Intigo')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function translateError(code: string, t: (k: string, fallback?: string) => string): string {
  switch (code) {
    case 'invalid_intigo_credentials':
      return t('settings.deliveryProviders.autoConfig.errInvalidCreds', 'Email ou mot de passe Intigo incorrect.');
    case 'webhook_url_must_be_https':
      return t('settings.deliveryProviders.autoConfig.errHttps', "L'URL webhook doit utiliser HTTPS.");
    case 'csrf_token_not_found':
    case 'csrf_cookie_not_set':
      return t('settings.deliveryProviders.autoConfig.errCsrf', "Le formulaire Intigo a changé. Configurez le webhook manuellement dans le dashboard Intigo.");
    case 'save_webhook_failed':
      return t('settings.deliveryProviders.autoConfig.errSave', "Intigo a refusé l'enregistrement du webhook. Vérifiez que votre compte est actif.");
    case 'missing_authorization':
      return t('settings.deliveryProviders.autoConfig.errAuth', 'Reconnectez-vous à l\'ERP et réessayez.');
    case 'invalid_authorization':
      return t('settings.deliveryProviders.autoConfig.errAuth', 'Reconnectez-vous a l ERP et reessayez.');
    case 'provider_not_configured':
      return t('settings.deliveryProviders.autoConfig.errProvider', "Enregistrez d'abord la configuration Intigo dans l'ERP, puis reessayez.");
    case 'rate_limited':
      return t('settings.deliveryProviders.autoConfig.errRateLimit', 'Trop de tentatives. Attendez une minute puis reessayez.');
    default:
      return code;
  }
}
